import { Card, Descriptions, Empty, Space, Timeline, Typography } from '@arco-design/web-react';
import { FLOW_STATE_LABELS, type FlowTrace } from '../api/cdr';
import { FLOW_TRAIL_NODE_LABELS, flowTraceEventMeta } from '../utils/labels';
import PillTag, { type PillColor } from './PillTag';


function fmtTime(t?: string): string {
  if (!t) return '-';
  const d = new Date(t);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d
    .getSeconds()
    .toString()
    .padStart(2, '0')}`;
}

/** 节点中文名：biz_flow_node.name 优先，常见 key 映射兜底，仍空则原样 */
function nodeNameOf(key?: string, name?: string): string {
  if (name && /[\u4e00-\u9fa5]/.test(name)) return name;
  return FLOW_TRAIL_NODE_LABELS[key || ''] || name || key || '';
}

/**
 * 事件 payload 精简单行：剔除 flow/flow_id/node 等已被主行中文化表达的键，
 * 其余字段序列化为单行（悬浮可见脱敏全量）。避免整段 dump 泄露流程 code。
 */
function payloadLine(payload?: string): { text: string; raw: string } {
  const raw = payload && payload !== '{}' ? payload : '';
  if (!raw) return { text: '', raw: '' };
  try {
    const m = JSON.parse(raw);
    if (m && typeof m === 'object' && !Array.isArray(m)) {
      const rest: Record<string, unknown> = { ...(m as Record<string, unknown>) };
      delete rest.flow;
      delete rest.flow_id;
      delete rest.node;
      const keys = Object.keys(rest);
      if (keys.length > 0) {
        const short: Record<string, unknown> = {};
        for (const k of keys) short[k] = rest[k];
        return { text: JSON.stringify(short), raw };
      }
      return { text: '', raw };
    }
  } catch {
    /* 非 JSON 原样展示 */
  }
  return { text: raw, raw };
}

function EvPayloadLine({ payload }: { payload?: string }) {
  const { text, raw } = payloadLine(payload);
  if (!text) return null;
  return (
    <div className="bcc-flow-payload" title={`payload：${raw}`}>
      {text}
    </div>
  );
}

export default function FlowTraceView({ trace, loading }: { trace: FlowTrace | null; loading?: boolean }) {
  if (loading) {
    return <Typography.Text type="secondary">加载中…</Typography.Text>;
  }
  if (!trace || !Array.isArray(trace.instances) || trace.instances.length === 0) {
    return (
      <Empty
        style={{ padding: '32px 0' }}
        description="该通话未进入业务流程（AI 直答 / 人工会话 / 运行时未挂载）"
      />
    );
  }
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {trace.instances.map(({ instance: inst, events }, gi) => (
        <Card
          key={gi}
          size="small"
          title={`流程轨迹 ${trace.instances.length > 1 ? `${gi + 1} · ` : ''}${inst.flow_name || `流程#${inst.flow_id}`}`}
        >
          <Descriptions
            column={3}
            size="small"
            data={[
              { label: '状态', value: <PillTag color={inst.state === 'completed' ? 'green' : 'arcoblue'}>{FLOW_STATE_LABELS[inst.state] || inst.state}</PillTag> },
              { label: '版本', value: `v${inst.flow_version}` },
              { label: '通道', value: inst.channel || '-' },
              { label: '当前节点', value: nodeNameOf(inst.current_node_key) || '-' },
              { label: '开始', value: fmtTime(inst.started_at) },
            ]}
          />

          {events.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Typography.Text type="secondary">事件时间线（{events.length}，PII 已脱敏）</Typography.Text>
              <Timeline style={{ marginTop: 6 }}>
                {events.map((e, i) => {
                  const [label, color] = flowTraceEventMeta(e.event);
                  const nodeName = nodeNameOf(e.node_key, e.node_name);
                  return (
                    <Timeline.Item key={i} dotColor={color}>
                      <div>
                        <div className="bcc-flow-event-main">
                          <PillTag size="sm" color={color as PillColor}>
                            {label}
                          </PillTag>
                          {nodeName && <span>{nodeName}</span>}
                          {e.flow_name && <Typography.Text type="secondary">{e.flow_name}</Typography.Text>}
                          <span className="bcc-flow-ev-time">{fmtTime(e.ts)}</span>
                        </div>
                        <EvPayloadLine payload={e.payload} />
                      </div>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </div>
          )}
        </Card>
      ))}
    </Space>
  );
}
